import { supabase } from '@/lib/supabase';
import type { ThemeRevision, ThemeRow } from '@/types';
import { migrateDoc, THEME_DOC_VERSION, type ThemeDoc } from '../model/doc';
import type { Json } from '@/types/database.types';

/**
 * Theme documents — Supabase CRUD + the transactional RPCs (publish / apply /
 * fork / restore). Single-row ops go through PostgREST; anything multi-step is
 * a Postgres function so it commits atomically.
 */

/** Thrown when an autosave hits a row someone else (another tab/device) updated. */
export class ThemeConflictError extends Error {
  constructor() {
    super('Theme was updated elsewhere');
    this.name = 'ThemeConflictError';
  }
}

export async function listMyThemes(): Promise<ThemeRow[]> {
  const { data, error } = await supabase
    .from('themes')
    .select('*')
    .neq('status', 'archived')
    .eq('is_preset', false)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function getTheme(id: string): Promise<ThemeRow | null> {
  const { data, error } = await supabase.from('themes').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return data;
}

/** Owner's current draft for a community (the studio's working doc), if any. */
export async function getCommunityDraft(communityId: string, ownerId: string): Promise<ThemeRow | null> {
  const { data, error } = await supabase
    .from('themes')
    .select('*')
    .eq('community_id', communityId)
    .eq('owner_id', ownerId)
    .eq('is_preset', false)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function createTheme(input: {
  ownerId: string;
  doc: ThemeDoc;
  name?: string;
  communityId?: string;
}): Promise<ThemeRow> {
  const { data, error } = await supabase
    .from('themes')
    .insert({
      owner_id: input.ownerId,
      community_id: input.communityId ?? null,
      name: input.name ?? 'Untitled theme',
      doc: input.doc as unknown as Json,
      doc_version: THEME_DOC_VERSION,
    })
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

/**
 * Autosave target. `expectedUpdatedAt` is an optimistic-concurrency guard —
 * if another tab/device saved since we loaded, no row matches and we surface a
 * conflict instead of silently clobbering.
 */
export async function updateThemeDoc(
  id: string,
  doc: ThemeDoc,
  expectedUpdatedAt: string,
  name?: string,
): Promise<ThemeRow> {
  const { data, error } = await supabase
    .from('themes')
    .update({ doc: doc as unknown as Json, doc_version: THEME_DOC_VERSION, ...(name ? { name } : {}) })
    .eq('id', id)
    .eq('updated_at', expectedUpdatedAt)
    .select('*')
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new ThemeConflictError();
  return data;
}

export async function renameTheme(id: string, name: string): Promise<void> {
  const { error } = await supabase.from('themes').update({ name }).eq('id', id);
  if (error) throw error;
}

export async function setThemeVisibility(id: string, visibility: 'private' | 'unlisted' | 'public'): Promise<void> {
  const { error } = await supabase.from('themes').update({ visibility }).eq('id', id);
  if (error) throw error;
}

export async function deleteTheme(id: string): Promise<void> {
  const { error } = await supabase.from('themes').delete().eq('id', id);
  if (error) throw error;
}

/* ── Transactional RPCs ──────────────────────────────────────────────────── */

export async function publishTheme(id: string): Promise<ThemeRow> {
  const { data, error } = await supabase.rpc('publish_theme', { p_theme_id: id });
  if (error) throw error;
  return data as unknown as ThemeRow;
}

export async function applyThemeToCommunity(themeId: string, communityId: string): Promise<void> {
  const { error } = await supabase.rpc('apply_theme_to_community', {
    p_theme_id: themeId,
    p_community_id: communityId,
  });
  if (error) throw error;
}

export async function clearCommunityTheme(communityId: string): Promise<void> {
  const { error } = await supabase.rpc('clear_community_theme', { p_community_id: communityId });
  if (error) throw error;
}

export async function forkTheme(id: string): Promise<ThemeRow> {
  const { data, error } = await supabase.rpc('fork_theme', { p_theme_id: id });
  if (error) throw error;
  return data as unknown as ThemeRow;
}

export async function restoreRevision(themeId: string, rev: number): Promise<ThemeRow> {
  const { data, error } = await supabase.rpc('restore_revision', { p_theme_id: themeId, p_rev: rev });
  if (error) throw error;
  return data as unknown as ThemeRow;
}

/* ── Revisions ───────────────────────────────────────────────────────────── */

export async function listRevisions(themeId: string, limit = 50): Promise<ThemeRevision[]> {
  const { data, error } = await supabase
    .from('theme_revisions')
    .select('*')
    .eq('theme_id', themeId)
    .order('rev', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

/** Periodic autosave checkpoint (the owner-side half of history; publish checkpoints are server-side). */
export async function checkpointRevision(themeId: string, doc: ThemeDoc, savedBy: string): Promise<void> {
  const { data } = await supabase
    .from('theme_revisions')
    .select('rev')
    .eq('theme_id', themeId)
    .order('rev', { ascending: false })
    .limit(1)
    .maybeSingle();
  const { error } = await supabase.from('theme_revisions').insert({
    theme_id: themeId,
    rev: (data?.rev ?? 0) + 1,
    doc: doc as unknown as Json,
    doc_version: THEME_DOC_VERSION,
    kind: 'autosave',
    saved_by: savedBy,
  });
  if (error) throw error;
}

/* ── Marketplace / presets ───────────────────────────────────────────────── */

export async function listPublicThemes(limit = 40): Promise<ThemeRow[]> {
  const { data, error } = await supabase
    .from('themes')
    .select('*')
    .eq('status', 'published')
    .eq('visibility', 'public')
    .eq('is_preset', false)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

export async function listPresets(): Promise<ThemeRow[]> {
  const { data, error } = await supabase
    .from('themes')
    .select('*')
    .eq('is_preset', true)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/** Parse + upgrade a row's doc safely. */
export function docOf(row: ThemeRow): ThemeDoc {
  return migrateDoc(row.doc);
}
